import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  Skeleton,
  StatusBadge,
  Timestamp,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import {
  formatCalendarDate,
  formatCurrency,
  formatDateInTz,
  formatProgressPercent,
} from "@/design-system/lib/format";
import { StatementDownloadBar } from "../components/StatementDownloadBar";
import { StatementCardPayPanel } from "../components/StatementCardPayPanel";
import { InvoicePayPanel } from "./InvoicePayPanel";
import {
  portalBuyerFinanceApi,
  type BuyerFinanceLoanLedger,
  type BuyerFinanceLoanRow,
  type BuyerFinancePeriod,
  type BuyerFinanceSummary,
  type BuyerPostedStatement,
  type PortalBorrowerInvoice,
} from "../api/borrowerFinance";

// External party portals — Buyer "Payments & ledger" (PR-A). A visual roll-up
// across the buyer's loans (totals + overall status + amortization progress)
// that drills into any single loan's full schedule + payment history.
// Read-only over the engine's existing servicing ledger — no money logic here.

// Deliberate product hide (owner ruling, 2026-07-13): card payments are not
// offered to any buyer for now. The backend card endpoints, surcharge policy,
// and admin grid stay intact — re-enabling is flipping this one constant.
const CARD_PAY_ENABLED = false;

const STATUS_LABEL: Record<string, string> = {
  current: "On track",
  overdue: "Overdue",
  "paid-off": "Paid off",
};

const PERIOD_LABEL: Record<string, string> = {
  "on-time": "On time",
  late: "Late",
  partial: "Partial",
  missed: "Missed",
  nsf: "NSF",
  prepaid: "Prepaid",
  upcoming: "Upcoming",
};

function loanStatusTone(status: string): StatusTone {
  if (status === "overdue") return "danger";
  if (status === "paid-off") return "ok";
  return "progress";
}

function periodTone(status: string): StatusTone {
  switch (status) {
    case "on-time":
    case "prepaid":
      return "ok";
    case "late":
    case "partial":
      return "warn";
    case "missed":
    case "nsf":
      return "danger";
    default:
      return "neutral";
  }
}

// One decimal below 1% so a real principal paydown never displays a
// misleading "0%" (shared with the portal Home payoff bars).
function pct(value: number): string {
  return formatProgressPercent(value);
}

function ProgressBar({ value }: { value: number }) {
  const width = `${Math.max(0, Math.min(1, value)) * 100}%`;
  return (
    <div
      className="h-space-2 w-full overflow-hidden rounded-pill border border-border-light bg-white-warm"
      role="progressbar"
      aria-valuenow={Math.round(value * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="h-full rounded-pill bg-gold" style={{ width }} />
    </div>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-space-1">
        <p className="text-caption uppercase tracking-wide text-gray-mid">{label}</p>
        <div className="text-h4 font-semibold text-onyx">{children}</div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-caption text-gray-mid">{label}</p>
      <p className="text-small font-medium text-onyx">{value}</p>
    </div>
  );
}

function LoanRollupCard({
  loan,
  onOpen,
}: {
  loan: BuyerFinanceLoanRow;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`buyer-loan-card-${loan.loanId}`}
      className="block w-full rounded-lg border border-border-light bg-white-pure p-space-4 text-left transition-colors hover:border-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
    >
      <div className="flex items-center justify-between gap-space-3">
        <span className="text-small font-semibold text-onyx">{loan.loanNumber}</span>
        <StatusBadge tone={loanStatusTone(loan.status)}>
          {STATUS_LABEL[loan.status] ?? loan.status}
        </StatusBadge>
      </div>
      <div className="mt-space-3 grid grid-cols-2 gap-space-3 sm:grid-cols-4">
        <Field label="Outstanding" value={formatCurrency(loan.outstandingBalance)} />
        <Field label="Paid to date" value={formatCurrency(loan.totalReceived)} />
        <Field
          label="Next due"
          value={loan.nextDueDate ? formatCalendarDate(loan.nextDueDate) : "—"}
        />
        <Field
          label="Monthly"
          /* BLA Core §2.4 — the amount actually debited (P&I plus the payment
             administration fee), falling back to the amortization figure when
             the wire omits it, which is a loan without the fee. */
          value={
            (loan.totalMonthlyDebit ?? loan.scheduledMonthlyPayment) > 0
              ? formatCurrency(loan.totalMonthlyDebit ?? loan.scheduledMonthlyPayment)
              : "—"
          }
        />
      </div>
      <div className="mt-space-3 space-y-space-1">
        <div className="flex justify-between text-caption text-gray-mid">
          <span>Amortization</span>
          <span>{pct(loan.percentPaid)} paid</span>
        </div>
        <ProgressBar value={loan.percentPaid} />
      </div>
    </button>
  );
}

function ScheduleTable({ schedule }: { schedule: BuyerFinancePeriod[] }) {
  if (schedule.length === 0) {
    return (
      <EmptyState
        title="No schedule yet"
        description="This loan has no amortization schedule on file."
      />
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border-light">
      <table className="w-full text-small">
        <thead>
          <tr className="border-b border-border-light bg-white-warm text-left text-caption uppercase tracking-wide text-gray-mid">
            <th className="px-space-3 py-space-2 font-medium">#</th>
            <th className="px-space-3 py-space-2 font-medium">Due</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Payment</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Principal</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Interest</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Received</th>
            <th className="px-space-3 py-space-2 text-right font-medium">Balance</th>
            <th className="px-space-3 py-space-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {schedule.map((p) => (
            <tr
              key={p.periodIndex}
              className="border-b border-border-light last:border-0"
            >
              <td className="px-space-3 py-space-2 text-gray-mid">{p.periodIndex + 1}</td>
              <td className="px-space-3 py-space-2 text-onyx">
                {formatCalendarDate(p.dueDate)}
              </td>
              <td className="px-space-3 py-space-2 text-right text-onyx">
                {formatCurrency(p.expectedAmount)}
              </td>
              <td className="px-space-3 py-space-2 text-right text-gray-mid">
                {formatCurrency(p.expectedPrincipal)}
              </td>
              <td className="px-space-3 py-space-2 text-right text-gray-mid">
                {formatCurrency(p.expectedInterest)}
              </td>
              <td className="px-space-3 py-space-2 text-right text-onyx">
                {p.receivedAmount > 0 ? formatCurrency(p.receivedAmount) : "—"}
              </td>
              <td className="px-space-3 py-space-2 text-right text-gray-mid">
                {p.principalBalanceAfter != null
                  ? formatCurrency(p.principalBalanceAfter)
                  : "—"}
              </td>
              <td className="px-space-3 py-space-2">
                <StatusBadge tone={periodTone(p.status)}>
                  {PERIOD_LABEL[p.status] ?? p.status}
                </StatusBadge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LoanLedgerView({
  loanId,
  onBack,
}: {
  loanId: string;
  onBack: () => void;
}) {
  const [ledger, setLedger] = useState<BuyerFinanceLoanLedger | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLedger(null);
    setError(null);
    void (async () => {
      try {
        const data = await portalBuyerFinanceApi.loan(loanId);
        if (!cancelled) setLedger(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load this loan.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loanId]);

  return (
    <div className="space-y-space-5">
      <button
        type="button"
        onClick={onBack}
        data-testid="buyer-ledger-back"
        className="text-small font-medium text-gold hover:underline"
      >
        ← Back to all loans
      </button>
      {error && <p className="text-small text-status-error">{error}</p>}
      {!ledger && !error && <Skeleton className="h-40 w-full" />}
      {ledger && (
        <>
          <div className="flex items-center justify-between gap-space-3">
            <h3 className="text-h4 font-semibold text-onyx">{ledger.loanNumber}</h3>
            <StatusBadge tone={loanStatusTone(ledger.status)}>
              {STATUS_LABEL[ledger.status] ?? ledger.status}
            </StatusBadge>
          </div>
          <div className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Outstanding balance">
              {formatCurrency(ledger.outstandingBalance)}
            </Stat>
            <Stat label="Next payment">
              {ledger.nextDueDate ? formatCalendarDate(ledger.nextDueDate) : "—"}
            </Stat>
            <Stat label="Monthly payment">
              {ledger.scheduledMonthlyPayment > 0
                ? formatCurrency(ledger.scheduledMonthlyPayment)
                : "—"}
            </Stat>
            <Stat label="Paid to date">{formatCurrency(ledger.totalReceived)}</Stat>
          </div>
          <Card>
            <CardContent className="space-y-space-2">
              <div className="flex justify-between text-small text-gray-mid">
                <span>Amortization progress</span>
                <span className="font-medium text-onyx">
                  {pct(ledger.percentPaid)} of principal paid
                </span>
              </div>
              <ProgressBar value={ledger.percentPaid} />
              {ledger.isOverdue && (
                <p className="text-caption text-status-error">
                  {ledger.daysDelinquent} day{ledger.daysDelinquent === 1 ? "" : "s"} past due
                </p>
              )}
            </CardContent>
          </Card>
          {/* WS-8 — the buyer's on-demand monthly activity download is
              "account activity", NOT a "statement": the persisted, payable
              bills below (BuyerStatementsSection) own that word. */}
          <StatementDownloadBar
            label="Download account activity"
            monthAriaLabel="Account activity month"
            onDownload={(p) => portalBuyerFinanceApi.statement(loanId, p)}
            filename={(p) => `account-activity-${ledger.loanNumber}-${p}.pdf`}
          />
          <ScheduleTable schedule={ledger.schedule} />
        </>
      )}
    </div>
  );
}

/** "2026-06-30" → "June 2026" (the billing month). */
function billingMonth(periodEnd: string): string {
  const d = new Date(`${periodEnd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return periodEnd;
  return d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

// PR-E — the buyer's Posted monthly statements/bills (TPS-approved). The
// rendered PDF opens in a new tab (view-only). PR-F adds paying the bill: when
// a bank mandate is on file for the loan, the buyer pays off-session with one
// button (ACH — no card form), and the row reflects the payment lifecycle.
// Renders nothing until loaded and only when there's at least one.
function BuyerStatementsSection() {
  const [rows, setRows] = useState<BuyerPostedStatement[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const data = await portalBuyerFinanceApi.postedStatements();
    setRows(data);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalBuyerFinanceApi.postedStatements();
        if (!cancelled) setRows(data);
      } catch {
        if (!cancelled) setError("Couldn't load your statements.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!rows || rows.length === 0) {
    return error ? (
      <p className="text-caption text-status-error" role="alert">
        {error}
      </p>
    ) : null;
  }

  return (
    <div className="space-y-space-3" data-testid="buyer-statements">
      <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid">
        Statements ({rows.length})
      </h3>
      {error && (
        <p className="text-caption text-status-error" role="alert">
          {error}
        </p>
      )}
      {rows.map((s) => (
        <StatementRow
          key={s.id}
          statement={s}
          reload={reload}
          onError={() => setError("Couldn't open that statement.")}
        />
      ))}
    </div>
  );
}

// ── SP-4 WS-3 — service-fee invoices (regulated vendors, billed through
// Phoenix). Deliberately NOT statements: distinct heading, distinct copy,
// paid interactively on the PHOENIX Stripe rails via the shared
// InvoicePayPanel — never the statement/autopay path.

const SERVICE_FEE_PAYABLE = new Set(["issued", "viewed", "overdue"]);

const SERVICE_FEE_STATUS_LABEL: Record<string, string> = {
  issued: "Due",
  viewed: "Due",
  processing: "Processing",
  paid: "Paid",
  overdue: "Overdue",
  void: "Cancelled",
};

function serviceFeeTone(status: string): StatusTone {
  switch (status) {
    case "paid":
      return "ok";
    case "processing":
      return "info";
    case "overdue":
      return "danger";
    case "void":
      return "muted";
    default:
      return "warn";
  }
}

function BuyerServiceFeesSection() {
  const [rows, setRows] = useState<PortalBorrowerInvoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalBuyerFinanceApi.serviceFeeInvoices();
        if (!cancelled) setRows(data);
      } catch {
        if (!cancelled) setError("Couldn't load your service fees.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The list owns the rows; a settled payment patches its row back in
  // (the vendor InvoicesTab onUpdated contract).
  const patchRow = (updated: PortalBorrowerInvoice) =>
    setRows((prev) =>
      prev ? prev.map((r) => (r.id === updated.id ? updated : r)) : prev,
    );

  if (!rows || rows.length === 0) {
    return error ? (
      <p className="text-caption text-status-error" role="alert">
        {error}
      </p>
    ) : null;
  }

  return (
    <div className="space-y-space-3" data-testid="buyer-service-fees">
      <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid">
        Service fees ({rows.length})
      </h3>
      <p className="text-caption text-gray-mid m-0">
        Fees from service providers working on your loan, billed through
        Phoenix Capital. These are separate from your monthly loan payment
        and never appear on your statement.
      </p>
      {rows.map((invoice) => (
        <ServiceFeeRow key={invoice.id} invoice={invoice} onUpdated={patchRow} />
      ))}
    </div>
  );
}

function ServiceFeeRow({
  invoice,
  onUpdated,
}: {
  invoice: PortalBorrowerInvoice;
  onUpdated: (updated: PortalBorrowerInvoice) => void;
}) {
  const toast = useToast();
  const [paying, setPaying] = useState(false);
  const payable = SERVICE_FEE_PAYABLE.has(invoice.status);

  return (
    <Card data-testid={`service-fee-${invoice.id}`}>
      <CardContent className="space-y-space-2">
        <div className="flex flex-wrap items-baseline justify-between gap-space-2">
          <div>
            <p className="text-small font-semibold text-onyx m-0">
              Service fee from {invoice.vendorName}
            </p>
            <p className="text-caption text-gray-mid m-0">
              Invoice {invoice.invoiceNumber}
              {invoice.dueDate
                ? ` · due ${formatCalendarDate(invoice.dueDate)}`
                : ""}
            </p>
          </div>
          <div className="flex items-center gap-space-2">
            <span className="text-h4 font-semibold text-onyx tabular-nums">
              {formatCurrency(invoice.amount)}
            </span>
            <StatusBadge
              tone={serviceFeeTone(invoice.status)}
              struck={invoice.status === "void"}
            >
              {SERVICE_FEE_STATUS_LABEL[invoice.status] ?? invoice.status}
            </StatusBadge>
          </div>
        </div>
        {invoice.description && (
          <p className="text-caption text-gray-mid m-0">{invoice.description}</p>
        )}
        <p className="text-caption text-gray-mid m-0">
          This is a service fee from {invoice.vendorName} — separate from your
          monthly payment.
        </p>
        {invoice.status === "processing" && (
          <p className="text-caption text-gray-mid m-0" data-testid="service-fee-processing">
            Your bank transfer is on its way — it takes a few business days to
            settle. Nothing more to do.
          </p>
        )}
        {invoice.status === "paid" && invoice.paidAt && (
          <p className="text-caption text-gray-mid m-0">
            Paid {formatCalendarDate(invoice.paidAt)}. Thank you.
          </p>
        )}
        {payable && !paying && (
          <Button
            size="sm"
            onClick={() => setPaying(true)}
            data-testid={`service-fee-pay-${invoice.id}`}
          >
            Pay this fee
          </Button>
        )}
        {payable && paying && (
          <InvoicePayPanel
            invoiceId={invoice.id}
            amountLabel={formatCurrency(invoice.amount)}
            createIntent={portalBuyerFinanceApi.createInvoicePaymentIntent}
            syncPayment={portalBuyerFinanceApi.syncInvoicePayment}
            onSettled={(updated, kind) => {
              setPaying(false);
              onUpdated(updated);
              toast.success(
                kind === "paid"
                  ? "Payment received — thank you."
                  : "Payment started — bank transfers take a few business days to settle.",
              );
            }}
            onCancel={() => setPaying(false)}
          />
        )}
      </CardContent>
    </Card>
  );
}

/** ACH is off-session: "Initiated"/"Processing" mean the bank debit is in
 *  flight (a few business days to settle) — there's nothing more to press. */
function isPaymentInFlight(paymentStatus: string | null): boolean {
  return paymentStatus === "Processing" || paymentStatus === "Initiated";
}

/** A surcharge-rate FRACTION (0.03) as a display percent ("3.00%"). No shared
 *  formatPercent helper exists in the design system. */
function formatRatePercent(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`;
}

function StatementRow({
  statement: s,
  reload,
  onError,
}: {
  statement: BuyerPostedStatement;
  reload: () => Promise<void>;
  onError: () => void;
}) {
  const toast = useToast();
  const [showCard, setShowCard] = useState(false);

  const pay = useSubmittingMutation(() => portalBuyerFinanceApi.payStatement(s.id), {
    refresh: reload,
    // Honest ACH expectation-setting right after the buyer initiates.
    onSuccess: (r) =>
      toast.success(
        `${r.message} Bank transfers take 3–5 business days — we'll confirm when it clears.`,
      ),
    onError: (err) =>
      toast.error(
        err instanceof Error ? err.message : "Couldn't start that payment.",
      ),
  });

  const openPdf = async () => {
    try {
      const blob = await portalBuyerFinanceApi.statementPdf(s.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      onError();
    }
  };

  // The card option is offered alongside ACH only while the bill is still
  // owed (a Paid / in-flight statement has nothing left to pay).
  const canPayByCard =
    CARD_PAY_ENABLED &&
    s.cardAllowed &&
    s.status === "Posted" &&
    !isPaymentInFlight(s.paymentStatus);

  return (
    <div
      data-testid={`buyer-statement-${s.id}`}
      className="rounded-lg border border-border-light bg-white-pure p-space-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-space-3">
        <div>
          <p className="text-small font-semibold text-onyx">
            {s.loanNumber} — {billingMonth(s.periodEnd)}
          </p>
          <p className="text-caption text-gray-mid">
            {formatCurrency(s.amountDue)} due
            {s.postedAt && ` · Posted ${formatDateInTz(s.postedAt)}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-space-3">
          <PayState statement={s} pay={pay} />
          {canPayByCard && !showCard && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowCard(true)}
              data-testid={`buyer-statement-card-open-${s.id}`}
            >
              Pay by card
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void openPdf()}
            data-testid={`buyer-statement-view-${s.id}`}
          >
            View PDF
          </Button>
        </div>
      </div>

      {canPayByCard && showCard && (
        <div className="mt-space-4 border-t border-border-light pt-space-4">
          <p
            className="mb-space-3 text-caption text-gray-mid"
            data-testid={`buyer-statement-card-disclosure-${s.id}`}
          >
            Credit cards incur a surcharge of up to {formatRatePercent(s.surchargeRate)};
            debit is not surcharged. The exact surcharge is shown before you pay.
          </p>
          <StatementCardPayPanel
            statementId={s.id}
            amountDue={s.amountDue}
            onSettled={reload}
            onCancel={() => setShowCard(false)}
          />
        </div>
      )}
    </div>
  );
}

function PayState({
  statement: s,
  pay,
}: {
  statement: BuyerPostedStatement;
  pay: ReturnType<typeof useSubmittingMutation<[], unknown>>;
}) {
  if (s.status === "Paid") {
    return (
      <span
        className="flex items-center gap-space-2"
        data-testid={`buyer-statement-paid-${s.id}`}
      >
        <StatusBadge tone="ok">Paid</StatusBadge>
        {s.paidAt && <Timestamp value={s.paidAt} />}
      </span>
    );
  }

  if (isPaymentInFlight(s.paymentStatus)) {
    return (
      <StatusBadge
        tone="muted"
        hint="Bank transfers take 3–5 business days — we'll confirm when it clears."
        data-testid={`buyer-statement-processing-${s.id}`}
      >
        Payment processing
      </StatusBadge>
    );
  }

  // SP-2b — autopay-enrolled loans draft automatically; "Pay now" stays as
  // a secondary affordance for buyers who want to settle early. A Returned
  // draft is never auto-retried (one attempt per statement), so it falls
  // through to the normal pay affordances below.
  if (s.autopayEnrolled && s.status === "Posted" && s.paymentStatus !== "Returned") {
    return (
      <span
        className="flex flex-col items-end gap-space-1"
        data-testid={`buyer-statement-autopay-${s.id}`}
      >
        <span className="flex items-center gap-space-2">
          <StatusBadge tone="ok">Autopay scheduled</StatusBadge>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={pay.pending}
            disabled={pay.pending}
            onClick={() => void pay.submit()}
            data-testid={`buyer-statement-pay-${s.id}`}
          >
            Pay now
          </Button>
        </span>
        <span className="text-caption text-gray-mid">
          we&apos;ll draft automatically on the due date
        </span>
      </span>
    );
  }

  if (s.mandateOnFile) {
    return (
      <span className="flex flex-col items-end gap-space-1">
        <Button
          type="button"
          variant="primary"
          size="sm"
          loading={pay.pending}
          disabled={pay.pending}
          onClick={() => void pay.submit()}
          data-testid={`buyer-statement-pay-${s.id}`}
        >
          {`Pay ${formatCurrency(s.amountDue)}`}
        </Button>
        {s.bankLast4 && (
          <span className="text-caption text-gray-mid">
            via bank ending {s.bankLast4}
          </span>
        )}
      </span>
    );
  }

  // SP-2b — self-serve bank setup now lives on the buyer's Home tab.
  return (
    <span
      className="flex max-w-[18rem] flex-col items-end gap-space-1 text-right text-caption text-gray-mid"
      data-testid={`buyer-statement-no-mandate-${s.id}`}
    >
      <span>
        Set up your bank account in a couple of minutes on your Home tab.
      </span>
      <span>Need help? Contact your servicer.</span>
      <GoToHomeTabButton statementId={s.id} />
    </span>
  );
}

/** Query-param tab hop (the shell reads ?tab= — same convention the sidebar
 *  uses), kept in its own component so the router hook only mounts when the
 *  no-mandate affordance renders. */
function GoToHomeTabButton({ statementId }: { statementId: string }) {
  const [, setParams] = useSearchParams();
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={() =>
        setParams(
          (prev) => {
            const updated = new URLSearchParams(prev);
            updated.set("tab", "home");
            return updated;
          },
          { replace: true },
        )
      }
      data-testid={`buyer-statement-go-home-${statementId}`}
    >
      Set up bank payment
    </Button>
  );
}

export function BorrowerPaymentsTab() {
  const [summary, setSummary] = useState<BuyerFinanceSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalBuyerFinanceApi.summary();
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your payments.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (selectedLoanId) {
    return (
      <LoanLedgerView loanId={selectedLoanId} onBack={() => setSelectedLoanId(null)} />
    );
  }

  if (error) return <p className="text-small text-status-error">{error}</p>;

  if (!summary) {
    return (
      <div
        className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="buyer-payments-loading"
      >
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (summary.loanCount === 0) {
    return (
      <EmptyState
        title="No loans yet"
        description="When your loan is funded and boarded, your balance, payment schedule, and history will appear here."
      />
    );
  }

  return (
    <div className="space-y-space-5">
      <div className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Outstanding balance">
          {formatCurrency(summary.totalOutstandingBalance)}
        </Stat>
        <Stat label="Paid to date">{formatCurrency(summary.totalPaidToDate)}</Stat>
        <Stat label="Next payment due">
          {summary.nextPaymentDueDate ? formatCalendarDate(summary.nextPaymentDueDate) : "—"}
          {summary.nextPaymentAmount > 0 && (
            <span className="ml-space-2 text-small font-normal text-gray-mid">
              {formatCurrency(summary.nextPaymentAmount)}
            </span>
          )}
        </Stat>
        <Stat label="Overall status">
          <StatusBadge tone={loanStatusTone(summary.overallStatus)}>
            {STATUS_LABEL[summary.overallStatus] ?? summary.overallStatus}
          </StatusBadge>
        </Stat>
      </div>

      <Card>
        <CardContent className="space-y-space-2">
          <div className="flex justify-between text-small text-gray-mid">
            <span>Portfolio amortization</span>
            <span className="font-medium text-onyx">
              {pct(summary.overallPercentPaid)} of principal paid
            </span>
          </div>
          <ProgressBar value={summary.overallPercentPaid} />
        </CardContent>
      </Card>

      <div className="space-y-space-3">
        <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid">
          Your loans ({summary.loanCount})
        </h3>
        {summary.loans.map((loan) => (
          <LoanRollupCard
            key={loan.loanId}
            loan={loan}
            onOpen={() => setSelectedLoanId(loan.loanId)}
          />
        ))}
      </div>

      <BuyerStatementsSection />

      <BuyerServiceFeesSection />
    </div>
  );
}
